import { useEffect, useState } from "react";
import type { DashboardData } from "../types";
import { api, ApiError } from "../api";
import ErrorState from "./ErrorState";
import CardDetalhe from "./CardDetalhe";
import { BarChart, HBars, PieChart } from "./charts";
import type { IconName } from "../icons";
import {
  IconFlask,
  IconClipboard,
  IconCheck,
  IconAlert,
  IconClock,
  IconSend,
  IconBox,
  IconTruck,
} from "../icons";

const COLORS = ["#0c7a3f", "#15a85a", "#34d27e", "#0a5c30", "#5cd99a", "#1f7a4d"];

export default function Dashboard({ refreshKey }: { refreshKey: number }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [reload, setReload] = useState(0);
  /** Card aberto (dois cliques): mostra os registros por trás do número. */
  const [aberto, setAberto] = useState<{ card: string; titulo: string; icone: IconName } | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .dashboard()
      .then((d) => alive && setData(d))
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof ApiError ? err : new ApiError(String(err)));
      });
    return () => {
      alive = false;
    };
  }, [refreshKey, reload]);

  if (error)
    return (
      <ErrorState
        message={error.message}
        code={error.code}
        onRetry={() => setReload((n) => n + 1)}
      />
    );
  if (!data) return <div className="empty">Carregando indicadores...</div>;

  const c = data.cards;
  // `card` é a chave que o servidor entende (server/dashboard-cards.ts): é ela
  // que faz o duplo clique abrir a lista certa por trás do número.
  const cards: CardPainel[] = [
    { card: "total_analises", icone: "flask", label: "Total de análises", value: c.total_analises, hint: "todas as OS", Icon: IconFlask, tone: "green" },
    { card: "recebidas", icone: "clipboard", label: "Recebidas", value: c.recebidas, hint: "aguardando análise", Icon: IconClipboard, tone: "emerald" },
    { card: "em_analise", icone: "history", label: "Em análise", value: c.em_analise, hint: "em andamento", Icon: IconClock, tone: "teal" },
    { card: "despachadas", icone: "check", label: "Despachadas", value: c.despachadas, hint: "laudo enviado ao Relatórios", Icon: IconSend, tone: "lime" },
    { card: "aprovadas", icone: "check", label: "Aprovadas", value: c.aprovadas, hint: `${c.com_ressalvas} com ressalvas`, Icon: IconCheck, tone: "green" },
    { card: "atrasadas", icone: "alert", label: "Atrasadas", value: c.atrasadas, hint: "fora do prazo de retorno", Icon: IconAlert, tone: "red" },
  ];

  // Módulos novos. `?? ` porque uma base ainda sem as tabelas devolve o
  // dashboard sem esses blocos — e o painel inteiro não pode sumir por isso.
  const q = data.quebras ?? { mes: 0, frascos_mes: 0, seringas_mes: 0, total: 0, porEtapa: [] };
  const m = data.material ?? { aguardando: 0, em_separacao: 0, separado: 0, enviado: 0, total: 0, divergentes: 0 };

  const cardsOperacao: CardPainel[] = [
    {
      card: "quebras_mes",
      icone: "alert",
      label: "Quebras no mês",
      value: q.mes,
      hint: `${q.frascos_mes} frasco(s) · ${q.seringas_mes} seringa(s)`,
      Icon: IconAlert,
      tone: q.mes > 0 ? "red" : "green",
    },
    {
      card: "quebras_total",
      icone: "alert",
      label: "Quebras (total)",
      value: q.total,
      hint: "desde o início do registro",
      Icon: IconAlert,
      tone: "teal",
    },
    {
      card: "material_separar",
      icone: "box",
      label: "Material a separar",
      value: m.aguardando + m.em_separacao,
      hint: `${m.aguardando} aguardando · ${m.em_separacao} em separação`,
      Icon: IconBox,
      tone: "emerald",
    },
    {
      card: "material_prontas",
      icone: "truck",
      label: "Prontas / enviadas",
      value: m.separado + m.enviado,
      hint: `${m.separado} fechada(s) · ${m.enviado} enviada(s)`,
      Icon: IconTruck,
      tone: "lime",
    },
    {
      // O número que o laboratório precisa ver: caixa fechada com o conferido
      // diferente do previsto. Zero é o normal; qualquer coisa acima disso é
      // uma remessa para revisar.
      card: "material_divergentes",
      icone: "alert",
      label: "Remessas com divergência",
      value: m.divergentes,
      hint: "fechadas com conferido ≠ previsto",
      Icon: IconAlert,
      tone: m.divergentes > 0 ? "red" : "green",
    },
  ];

  return (
    <div className="dashboard">
      <div className="dash-topo">
        <h3 className="dash-secao">Análises</h3>
        <span className="muted dash-dica">Dois cliques em qualquer card para ver quais são</span>
      </div>
      <div className="cards">
        {cards.map((card) => (
          <Card key={card.label} card={card} onAbrir={setAberto} />
        ))}
      </div>

      <h3 className="dash-secao">Operação do laboratório</h3>
      <div className="cards">
        {cardsOperacao.map((card) => (
          <Card key={card.label} card={card} onAbrir={setAberto} />
        ))}
      </div>

      {aberto && (
        <CardDetalhe
          card={aberto.card}
          titulo={aberto.titulo}
          icone={aberto.icone}
          onCancel={() => setAberto(null)}
        />
      )}

      <div className="charts">
        {q.porEtapa.length > 0 && (
          <ChartBox title="Quebras por etapa (6 meses)">
            <HBars data={q.porEtapa} valueKey="total" labelKey="etapa" color="#c98a14" />
          </ChartBox>
        )}

        <ChartBox title="Análises por status">
          <PieChart
            data={data.porStatus}
            valueKey="total"
            labelKey="status"
            colors={["#c98a14", "#0e9488", "#0c7a3f"]}
          />
        </ChartBox>

        <ChartBox title="Resultado das análises">
          <PieChart
            data={data.porResultado}
            valueKey="total"
            labelKey="resultado"
            colors={["#0c7a3f", "#dc2626", "#c98a14"]}
          />
        </ChartBox>

        <ChartBox title="Análises por mês (6 meses)">
          <BarChart
            data={data.porMes}
            xKey="mes"
            series={[
              { key: "recebidas", label: "Recebidas", color: "#0c7a3f" },
              { key: "despachadas", label: "Despachadas", color: "#34d27e" },
            ]}
          />
        </ChartBox>
      </div>
    </div>
  );
}

/** Um card do painel: o número, o rótulo e a chave que abre a lista por trás. */
interface CardPainel {
  /** Chave que o servidor entende (server/dashboard-cards.ts). */
  card: string;
  icone: IconName;
  label: string;
  value: number;
  hint?: string;
  Icon: (p: { size?: number }) => JSX.Element;
  tone: string;
}

/**
 * Card clicável.
 *
 * Fica FORA do Dashboard de propósito: declarado dentro, o React trataria cada
 * render como um componente novo e remontaria os cards a cada atualização do
 * painel — perdendo animação e foco sem motivo.
 */
function Card({
  card,
  onAbrir,
}: {
  card: CardPainel;
  onAbrir: (a: { card: string; titulo: string; icone: IconName }) => void;
}) {
  const abrir = () => onAbrir({ card: card.card, titulo: card.label, icone: card.icone });
  return (
    <div
      className={`card tone-${card.tone} card-clicavel`}
      // Dois cliques, igual ao resto do sistema (linha de tabela abre assim).
      onDoubleClick={abrir}
      // Teclado: quem navega por Tab também precisa conseguir abrir.
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          abrir();
        }
      }}
      title={`Dois cliques para ver ${card.label.toLowerCase()}`}
    >
      <div className="card-icon">
        <card.Icon size={22} />
      </div>
      <div className="card-body">
        <span className="card-value">{card.value}</span>
        <span className="card-label">{card.label}</span>
        {card.hint && <span className="card-hint">{card.hint}</span>}
      </div>
    </div>
  );
}

function ChartBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="chart-box">
      <h3>{title}</h3>
      {children}
    </div>
  );
}
